import {useEffect, useRef, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {Hint} from '@/components/ui/hint'
import {AccionesDeFila} from '@/components/ui/acciones-de-fila'
import {NativeSelect} from '@/components/native-select'
import {FieldError} from '@/components/field-error'
import type {PerfilLineaRead} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {tope} from '@/lib/campos-texto'
import {validateRequired} from '@/lib/validation'
import {CLASES_GTC, claveDePeligro, type AperturaRiesgo, type RiesgoEscrito} from '@/lib/perfil-bloques'

/** KAI-334, FB-21. Medidas multilínea porque un `<input>` aplasta los renglones (KAI-243); se
 * cierra solo al éxito, así un 409 deja lo escrito. */
export function PerfilRiesgoDialog(props: {
  apertura: AperturaRiesgo | null
  peligrosGuardados: readonly PerfilLineaRead[]
  pending: boolean
  error: string | null
  onCerrar: () => void
  onGuardar: (escrito: RiesgoEscrito) => void
}) {
  // Un formulario nuevo por apertura (`key`): el estado nace de lo que se abre.
  if (!props.apertura) return null
  const clave = props.apertura.modo === 'editar' ? props.apertura.riesgo.peligro.clave : 'nuevo'
  return <FormularioDeRiesgo key={clave} {...props} apertura={props.apertura} />
}

type Renglon = {id: string; clave: string | null; texto: string}

function renglonesIniciales(apertura: AperturaRiesgo): Renglon[] {
  const guardadas = apertura.modo === 'editar' ? apertura.riesgo.medidas : []
  const renglones = guardadas.map((medida) => ({id: medida.clave, clave: medida.clave, texto: medida.texto}))
  return renglones.length > 0 ? renglones : [{id: 'nueva-0', clave: null, texto: ''}]
}

const idDeMedida = (id: string) => `perfil-riesgo-medida-${id}`

function FormularioDeRiesgo({
  apertura,
  peligrosGuardados,
  pending,
  error,
  onCerrar,
  onGuardar,
}: {
  apertura: AperturaRiesgo
  peligrosGuardados: readonly PerfilLineaRead[]
  pending: boolean
  error: string | null
  onCerrar: () => void
  onGuardar: (escrito: RiesgoEscrito) => void
}) {
  const {t} = useTranslation()
  const peligro = apertura.modo === 'editar' ? apertura.riesgo.peligro : null
  const [clase, setClase] = useState(peligro?.clasificacion_gtc ?? '')
  const [descripcion, setDescripcion] = useState(peligro?.texto ?? '')
  const [renglones, setRenglones] = useState<Renglon[]>(() => renglonesIniciales(apertura))
  const [contador, setContador] = useState(1)
  const [errorDescripcion, setErrorDescripcion] = useState<string | undefined>()
  const [erroresMedida, setErroresMedida] = useState<Record<string, string>>({})
  // Se consume una vez: si no, escribir en otro renglón devolvería el foco al último agregado.
  const enfocar = useRef<string | null>(null)
  useEffect(() => {
    if (!enfocar.current) return
    document.getElementById(enfocar.current)?.focus()
    enfocar.current = null
  }, [renglones])

  // La de un peligro de la Matriz es de su fila aunque la línea no la traiga; el servidor decide
  // igual en `_previa_actualizada`.
  const claseEditable =
    peligro === null ||
    peligro.origen_tipo === 'escrito_a_mano' ||
    (peligro.origen_tipo === 'catalogo_inci' && !peligro.clasificacion_gtc)
  const claseFija = !claseEditable
  // El servidor no borra una clase puesta.
  const ofreceSinClase = !peligro?.clasificacion_gtc

  function errorDeDescripcion(valor: string): string | undefined {
    const vacio = validateRequired(valor)
    if (vacio) return vacio
    // Misma clase y descripción son una sola clave: el segundo se tragaría las medidas del primero.
    const clave = claveDePeligro(clase, valor)
    const repetido = peligrosGuardados.some((otro) => otro.clave !== peligro?.clave && otro.grupo_clave === clave)
    return repetido ? 'perfilCargo.riesgo.duplicado' : undefined
  }

  function agregar() {
    const id = `nueva-${contador}`
    setContador(contador + 1)
    setRenglones([...renglones, {id, clave: null, texto: ''}])
    enfocar.current = idDeMedida(id)
  }

  function quitar(id: string) {
    const indice = renglones.findIndex((renglon) => renglon.id === id)
    const quedan = renglones.filter((renglon) => renglon.id !== id)
    setRenglones(quedan)
    const vecino = quedan[indice] ?? quedan[indice - 1]
    enfocar.current = vecino ? idDeMedida(vecino.id) : 'perfil-riesgo-descripcion'
  }

  function guardar(event: FormEvent) {
    event.preventDefault()
    const enDescripcion = errorDeDescripcion(descripcion)
    // Una medida guardada que se vacía no se borra por omisión: se quita con 🗑.
    const enMedidas = Object.fromEntries(
      renglones
        .filter((renglon) => renglon.clave !== null && renglon.texto.trim() === '')
        .map((renglon) => [renglon.id, 'perfilCargo.riesgo.medidaVacia'])
    )
    setErrorDescripcion(enDescripcion)
    setErroresMedida(enMedidas)
    if (enDescripcion || Object.keys(enMedidas).length > 0) return
    onGuardar({clase, descripcion, medidas: renglones.map(({clave, texto}) => ({clave, texto}))})
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="max-h-[90dvh] max-w-lg overflow-y-auto" data-testid="perfil-riesgo">
        <DialogHeader>
          <DialogTitle>{t(peligro ? 'perfilCargo.riesgo.titulo' : 'perfilCargo.riesgo.tituloNuevo')}</DialogTitle>
        </DialogHeader>
        <form noValidate onSubmit={guardar}>
          <fieldset className="space-y-3" disabled={pending}>
            <div className="space-y-2">
              <Label htmlFor="perfil-riesgo-clase">{t('perfilCargo.detalle.claseGtc')}</Label>
              <NativeSelect
                id="perfil-riesgo-clase"
                data-testid="perfil-riesgo-clase"
                value={clase}
                disabled={claseFija}
                onChange={(event) => setClase(event.target.value)}>
                {ofreceSinClase && <option value="">{t('perfilCargo.tabla.sinClase')}</option>}
                {CLASES_GTC.map((opcion) => (
                  <option key={opcion} value={opcion}>
                    {t(`ipevr.clasificacion.${opcion}`)}
                  </option>
                ))}
              </NativeSelect>
              {claseFija && (
                <Hint testId="perfil-riesgo-clase-fija">
                  {t(
                    peligro?.origen_tipo === 'riesgo_fila'
                      ? 'perfilCargo.riesgo.claseDeLaMatriz'
                      : 'perfilCargo.riesgo.claseDeSuOrigen'
                  )}
                </Hint>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="perfil-riesgo-descripcion">{t('perfilCargo.tabla.descripcionPeligro')}</Label>
              <Textarea
                maxLength={tope('TEXTO2000')}
                id="perfil-riesgo-descripcion"
                data-testid="perfil-riesgo-descripcion"
                value={descripcion}
                onBlur={(event) => setErrorDescripcion(errorDeDescripcion(event.target.value))}
                onChange={(event) => {
                  setDescripcion(event.target.value)
                  setErrorDescripcion(undefined)
                }}
              />
              <FieldError error={errorDescripcion} />
            </div>
            <div className="space-y-2" role="group" aria-labelledby="perfil-riesgo-medidas">
              <div className="flex items-center justify-between gap-1">
                <Label id="perfil-riesgo-medidas">{t('perfilCargo.tabla.medidas')}</Label>
                <AccionesDeFila
                  acciones={[
                    {
                      clave: 'agregar-medida',
                      concepto: 'agregar',
                      etiqueta: t('perfilCargo.detalle.agregarMedida'),
                      onActivar: agregar,
                    },
                  ]}
                />
              </div>
              <ul className="space-y-2">
                {renglones.map((renglon, indice) => {
                  const nombre = t('perfilCargo.riesgo.medidaN', {n: indice + 1})
                  return (
                    <li
                      key={renglon.id}
                      className="flex items-start gap-1"
                      data-testid={`perfil-riesgo-renglon-${indice}`}>
                      <div className="min-w-0 flex-1 space-y-1">
                        <Textarea
                          maxLength={tope('TEXTO2000')}
                          minRows={1}
                          rows={1}
                          id={idDeMedida(renglon.id)}
                          aria-label={nombre}
                          data-testid={`perfil-riesgo-medida-${indice}`}
                          value={renglon.texto}
                          onChange={(event) => {
                            const texto = event.target.value
                            setRenglones(renglones.map((otro) => (otro.id === renglon.id ? {...otro, texto} : otro)))
                            setErroresMedida({...erroresMedida, [renglon.id]: ''})
                          }}
                        />
                        <FieldError error={erroresMedida[renglon.id] || undefined} />
                      </div>
                      <AccionesDeFila
                        acciones={[
                          {
                            clave: `quitar-medida-${indice}`,
                            concepto: 'borrar',
                            etiqueta: t('perfilCargo.riesgo.quitarMedida'),
                            onActivar: () => quitar(renglon.id),
                          },
                        ]}
                        contexto={renglon.texto.trim() || nombre}
                      />
                    </li>
                  )
                })}
              </ul>
            </div>
            {error && (
              <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="perfil-riesgo-error">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onCerrar}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" size="sm" data-testid="perfil-riesgo-guardar">
                {t(pending ? 'common.saving' : 'perfilCargo.tabla.guardar')}
              </Button>
            </DialogFooter>
          </fieldset>
        </form>
      </DialogContent>
    </Dialog>
  )
}
